import { db } from './firebase';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';

export interface UserSettings {
  notifications: boolean;
  autoConnect: boolean;
  darkMode: boolean;
  slippageTolerance: string;
  soundEnabled: boolean;
  emailNotifications: boolean;
  showOnlineStatus: boolean;
  autoMarkAsRead: boolean;
  transactionSpeed: 'slow' | 'normal' | 'fast';
}

export const defaultSettings: UserSettings = {
  notifications: true,
  autoConnect: false,
  darkMode: true,
  slippageTolerance: '1.0',
  soundEnabled: true,
  emailNotifications: false,
  showOnlineStatus: true,
  autoMarkAsRead: true,
  transactionSpeed: 'normal',
};

export class SettingsService {
  /**
   * Récupérer les paramètres d'un utilisateur
   */
  static async getUserSettings(userId: string): Promise<UserSettings> {
    try {
      const settingsRef = doc(db, 'userSettings', userId);
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        // Merger avec les paramètres par défaut pour gérer les nouveaux champs
        return { ...defaultSettings, ...data } as UserSettings;
      }
      
      // Créer les paramètres par défaut si ils n'existent pas
      await setDoc(settingsRef, {
        ...defaultSettings,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return defaultSettings;
    } catch (error) {
      console.error('Erreur lors de la récupération des paramètres:', error);
      return defaultSettings;
    }
  }

  /**
   * Mettre à jour les paramètres d'un utilisateur
   */
  static async updateUserSettings(userId: string, settings: Partial<UserSettings>): Promise<boolean> {
    try {
      const settingsRef = doc(db, 'userSettings', userId);
      
      // Ajouter la date de mise à jour
      const updateData = {
        ...settings,
        updatedAt: new Date()
      };
      
      await updateDoc(settingsRef, updateData);
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des paramètres:', error);
      
      // Si le document n'existe pas, le créer avec les paramètres par défaut
      try {
        const currentSettings = await this.getUserSettings(userId);
        const newSettings = { 
          ...currentSettings, 
          ...settings,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        const newSettingsRef = doc(db, 'userSettings', userId);
        await setDoc(newSettingsRef, newSettings);
        return true;
      } catch (createError) {
        console.error('Erreur lors de la création des paramètres:', createError);
        return false;
      }
    }
  }

  /**
   * Réinitialiser les paramètres aux valeurs par défaut
   */
  static async resetUserSettings(userId: string): Promise<boolean> {
    try {
      const settingsRef = doc(db, 'userSettings', userId);
      const resetSettings = {
        ...defaultSettings,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      await setDoc(settingsRef, resetSettings);
      return true;
    } catch (error) {
      console.error('Erreur lors de la réinitialisation des paramètres:', error);
      return false;
    }
  }

  /**
   * Exporter les données utilisateur (paramètres + profil)
   */
  static async exportUserData(userId: string): Promise<any> {
    try {
      // Récupérer les paramètres
      const settings = await this.getUserSettings(userId);
      
      // Récupérer le profil utilisateur
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : null;

      return {
        exportDate: new Date().toISOString(),
        userId,
        settings,
        profile: userData,
      };
    } catch (error) {
      console.error('Erreur lors de l\'export des données:', error);
      return null;
    }
  }

  /**
   * Supprimer toutes les données utilisateur
   */
  static async deleteUserData(userId: string): Promise<boolean> {
    try {
      // Supprimer les paramètres
      const settingsRef = doc(db, 'userSettings', userId);
      await updateDoc(settingsRef, { deleted: true, deletedAt: new Date() });
      
      // Marquer le profil comme supprimé (soft delete)
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { 
        deleted: true, 
        deletedAt: new Date(),
        displayName: '[Utilisateur supprimé]',
        bio: '',
        profilePicture: ''
      });
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression des données:', error);
      return false;
    }
  }
}
